import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  TimeEntry,
  Expense,
  Bill,
  InvoiceTheme,
  TimeEntryStatistics,
  ExpenseStatistics,
  BillStatistics,
  TimeEntryFilters,
  ExpenseFilters,
  BillFilters,
  BillingTab,
  DrawerMode,
  DrawerType,
} from '../types/billing.types';

export interface BillingState {
  // Data
  timeEntries: TimeEntry[];
  expenses: Expense[];
  bills: Bill[];
  timeEntryStats: TimeEntryStatistics | null;
  expenseStats: ExpenseStatistics | null;
  billStats: BillStatistics | null;
  timeEntryPagination: { total: number; offset: number; has_more: boolean };
  expensePagination: { total: number; offset: number; has_more: boolean };
  billPagination: { total: number; offset: number; has_more: boolean };

  // UI state
  _loadingCount: number;
  loading: boolean;
  error: string | null;
  activeTab: BillingTab;

  // Drawer
  drawerOpen: boolean;
  drawerMode: DrawerMode;
  drawerType: DrawerType;
  selectedTimeEntryId: string | null;
  selectedExpenseId: string | null;
  selectedBillId: string | null;

  // Filters
  timeEntryFilters: TimeEntryFilters;
  expenseFilters: ExpenseFilters;
  billFilters: BillFilters;

  // Themes
  themes: InvoiceTheme[];
  selectedThemeId: string | null;

  // Single bill detail
  currentBill: Bill | null;
  currentLineItems: import('../types/billing.types').LineItem[];

  // Bulk selection
  selectedTimeEntryIds: string[];
  selectedExpenseIds: string[];
  selectedBillIds: string[];

  // Actions
  setTimeEntries: (entries: TimeEntry[], pagination: { total: number; offset: number; has_more: boolean }) => void;
  setExpenses: (expenses: Expense[], pagination: { total: number; offset: number; has_more: boolean }) => void;
  setBills: (bills: Bill[], pagination: { total: number; offset: number; has_more: boolean }) => void;
  setTimeEntryStats: (stats: TimeEntryStatistics) => void;
  setExpenseStats: (stats: ExpenseStatistics) => void;
  setBillStats: (stats: BillStatistics) => void;
  incrementLoading: () => void;
  decrementLoading: () => void;
  setError: (error: string | null) => void;
  setActiveTab: (tab: BillingTab) => void;
  openDrawer: (type: DrawerType, mode: DrawerMode, id?: string) => void;
  closeDrawer: () => void;
  setTimeEntryFilters: (filters: Partial<TimeEntryFilters>) => void;
  setExpenseFilters: (filters: Partial<ExpenseFilters>) => void;
  setBillFilters: (filters: Partial<BillFilters>) => void;
  resetFilters: () => void;
  toggleTimeEntrySelection: (id: string) => void;
  toggleExpenseSelection: (id: string) => void;
  toggleBillSelection: (id: string) => void;
  clearSelections: () => void;
  selectAllTimeEntries: () => void;
  selectAllExpenses: () => void;
  selectAllBills: () => void;

  // Theme actions
  setThemes: (themes: InvoiceTheme[]) => void;
  setSelectedThemeId: (id: string | null) => void;

  // Theme editor state
  editingTheme: Partial<InvoiceTheme> | null;
  setEditingTheme: (theme: Partial<InvoiceTheme> | null) => void;
  updateEditingThemeField: (field: string, value: unknown) => void;

  // Single bill detail actions
  setCurrentBill: (bill: Bill | null) => void;
  setCurrentLineItems: (items: import('../types/billing.types').LineItem[]) => void;
}

const DEFAULT_TIME_ENTRY_FILTERS: TimeEntryFilters = {
  limit: 50,
  offset: 0,
  order_by: 'entry_date',
  order_direction: 'DESC',
};

const DEFAULT_EXPENSE_FILTERS: ExpenseFilters = {
  limit: 50,
  offset: 0,
  order_by: 'expense_date',
  order_direction: 'DESC',
};

const DEFAULT_BILL_FILTERS: BillFilters = {
  limit: 50,
  offset: 0,
  order_by: 'issue_date',
  order_direction: 'DESC',
};

export const useBillingStore = create<BillingState>()(
  devtools(
    (set, get) => ({
      // Data defaults
      timeEntries: [],
      expenses: [],
      bills: [],
      timeEntryStats: null,
      expenseStats: null,
      billStats: null,
      timeEntryPagination: { total: 0, offset: 0, has_more: false },
      expensePagination: { total: 0, offset: 0, has_more: false },
      billPagination: { total: 0, offset: 0, has_more: false },

      // UI defaults
      _loadingCount: 0,
      loading: false,
      error: null,
      activeTab: 'time-entries',

      // Drawer defaults
      drawerOpen: false,
      drawerMode: 'create',
      drawerType: 'time-entry',
      selectedTimeEntryId: null,
      selectedExpenseId: null,
      selectedBillId: null,

      // Filter defaults
      timeEntryFilters: { ...DEFAULT_TIME_ENTRY_FILTERS },
      expenseFilters: { ...DEFAULT_EXPENSE_FILTERS },
      billFilters: { ...DEFAULT_BILL_FILTERS },

      // Theme defaults
      themes: [],
      selectedThemeId: null,
      editingTheme: null,

      // Single bill detail defaults
      currentBill: null,
      currentLineItems: [],

      // Selection defaults
      selectedTimeEntryIds: [],
      selectedExpenseIds: [],
      selectedBillIds: [],

      // Actions
      setTimeEntries: (entries, pagination) =>
        set({ timeEntries: entries, timeEntryPagination: pagination }),

      setExpenses: (expenses, pagination) =>
        set({ expenses, expensePagination: pagination }),

      setBills: (bills, pagination) =>
        set({ bills, billPagination: pagination }),

      setTimeEntryStats: (stats) => set({ timeEntryStats: stats }),
      setExpenseStats: (stats) => set({ expenseStats: stats }),
      setBillStats: (stats) => set({ billStats: stats }),
      incrementLoading: () =>
        set((s) => {
          const count = s._loadingCount + 1;
          return { _loadingCount: count, loading: true };
        }),
      decrementLoading: () =>
        set((s) => {
          const count = Math.max(0, s._loadingCount - 1);
          return { _loadingCount: count, loading: count > 0 };
        }),
      setError: (error) => set({ error }),
      setActiveTab: (tab) => set({ activeTab: tab, selectedTimeEntryIds: [], selectedExpenseIds: [], selectedBillIds: [] }),

      openDrawer: (type, mode, id) =>
        set({
          drawerOpen: true,
          drawerType: type,
          drawerMode: mode,
          selectedTimeEntryId: type === 'time-entry' ? (id ?? null) : null,
          selectedExpenseId: type === 'expense' ? (id ?? null) : null,
          selectedBillId: type === 'bill' ? (id ?? null) : null,
        }),

      closeDrawer: () =>
        set({
          drawerOpen: false,
          selectedTimeEntryId: null,
          selectedExpenseId: null,
          selectedBillId: null,
        }),

      setTimeEntryFilters: (filters) =>
        set((s) => ({
          timeEntryFilters: { ...s.timeEntryFilters, ...filters, offset: 0 },
        })),

      setExpenseFilters: (filters) =>
        set((s) => ({
          expenseFilters: { ...s.expenseFilters, ...filters, offset: 0 },
        })),

      setBillFilters: (filters) =>
        set((s) => ({
          billFilters: { ...s.billFilters, ...filters, offset: 0 },
        })),

      resetFilters: () =>
        set({
          timeEntryFilters: { ...DEFAULT_TIME_ENTRY_FILTERS },
          expenseFilters: { ...DEFAULT_EXPENSE_FILTERS },
          billFilters: { ...DEFAULT_BILL_FILTERS },
        }),

      toggleTimeEntrySelection: (id) =>
        set((s) => ({
          selectedTimeEntryIds: s.selectedTimeEntryIds.includes(id)
            ? s.selectedTimeEntryIds.filter((x) => x !== id)
            : [...s.selectedTimeEntryIds, id],
        })),

      toggleExpenseSelection: (id) =>
        set((s) => ({
          selectedExpenseIds: s.selectedExpenseIds.includes(id)
            ? s.selectedExpenseIds.filter((x) => x !== id)
            : [...s.selectedExpenseIds, id],
        })),

      toggleBillSelection: (id) =>
        set((s) => ({
          selectedBillIds: s.selectedBillIds.includes(id)
            ? s.selectedBillIds.filter((x) => x !== id)
            : [...s.selectedBillIds, id],
        })),

      clearSelections: () =>
        set({ selectedTimeEntryIds: [], selectedExpenseIds: [], selectedBillIds: [] }),

      selectAllTimeEntries: () =>
        set((s) => ({
          selectedTimeEntryIds:
            s.selectedTimeEntryIds.length === s.timeEntries.length
              ? []
              : s.timeEntries.map((e) => e.time_entry_id),
        })),

      selectAllExpenses: () =>
        set((s) => ({
          selectedExpenseIds:
            s.selectedExpenseIds.length === s.expenses.length
              ? []
              : s.expenses.map((e) => e.expense_id),
        })),

      selectAllBills: () =>
        set((s) => ({
          selectedBillIds:
            s.selectedBillIds.length === s.bills.length
              ? []
              : s.bills.map((b) => b.bill_id),
        })),

      // Theme actions
      setThemes: (themes) => set({ themes }),
      setSelectedThemeId: (id) => set({ selectedThemeId: id }),
      setEditingTheme: (theme) => set({ editingTheme: theme }),
      updateEditingThemeField: (field, value) =>
        set((s) => ({
          editingTheme: s.editingTheme ? { ...s.editingTheme, [field]: value } : null,
        })),

      // Single bill detail actions
      setCurrentBill: (bill) => set({ currentBill: bill }),
      setCurrentLineItems: (items) => set({ currentLineItems: items }),
    }),
    { name: 'edgelex-billing' }
  )
);
